import React, { useState } from 'react';
import { Save, Lock, User } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { ROLES } from '../../utils/constants';
import { getInitials, getAvatarColor, formatDate } from '../../utils/helpers';
import Button from '../common/Button';
import { logger } from '../../utils/logger';

const ProfileTab = () => {
  const { currentUser, updateCurrentUser, changePassword } = useAuth();
  const { updateUser } = useData();
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  
  const [profileForm, setProfileForm] = useState({
    name: currentUser?.name || '',
    phone: currentUser?.phone || '',
    fonction: currentUser?.fonction || '',
    originCity: currentUser?.originCity || ''
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});

  const role = ROLES[currentUser?.role] || ROLES.membre;

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!profileForm.name.trim()) {
      setErrors({ name: 'Le nom est obligatoire' });
      return;
    }

    try {
      // Mettre à jour le profil (sans l'email)
      const updatedData = {
        name: profileForm.name,
        phone: profileForm.phone,
        fonction: profileForm.fonction,
        originCity: profileForm.originCity
      };

      await updateUser(currentUser.id, updatedData);
      await updateCurrentUser(updatedData);
      setIsEditingProfile(false);
      setErrors({});
      alert('? Profil mis à jour avec succès !');
    } catch (error) {
      logger.error('Erreur mise à jour profil:', error);
      alert('? Erreur lors de la mise à jour du profil');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    
    if (!passwordForm.currentPassword) {
      newErrors.currentPassword = 'Mot de passe actuel requis';
    }
    
    if (!passwordForm.newPassword) {
      newErrors.newPassword = 'Nouveau mot de passe requis';
    } else if (passwordForm.newPassword.length < 6) {
      newErrors.newPassword = 'Minimum 6 caractères';
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      // Changer le mot de passe avec Firebase Auth
      const result = await changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword
      );
      
      if (result.success) {
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setIsEditingPassword(false);
        setErrors({});
        alert('? Mot de passe modifié avec succès !');
      } else {
        setErrors({ currentPassword: result.error });
      }
    } catch (error) {
      logger.error('Erreur changement mot de passe:', error);
      setErrors({ currentPassword: 'Erreur lors du changement de mot de passe' });
    }
  };

  return (
    <div className="space-y-6">
      {/* Avatar et Informations de base */}
      <div className="flex items-center space-x-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-600 rounded-lg">
        <div className={`w-24 h-24 ${getAvatarColor(currentUser?.name)} rounded-full flex items-center justify-center text-white font-bold text-3xl`}>
          {getInitials(currentUser?.name)}
        </div>
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
            {currentUser?.name}
          </h2>
          <div className="flex items-center space-x-2 mt-2">
            <span className={`px-3 py-1 ${role.bgLight} ${role.textColor} text-sm font-medium rounded-full`}>
              {role.label}
            </span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Membre depuis {formatDate(currentUser?.joinDate)}
          </p>
        </div>
      </div>

      {/* Informations du profil */}
      <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center">
            <User size={20} className="mr-2" />
            Informations personnelles
          </h3>
          {!isEditingProfile && (
            <Button
              onClick={() => setIsEditingProfile(true)}
              variant="outline"
              size="sm"
            >
              Modifier
            </Button>
          )}
        </div>

        {isEditingProfile ? (
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nom et Prénoms *
              </label>
              <input
                type="text"
                value={profileForm.name}
                onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                className={`w-full border rounded-lg px-4 py-2 bg-white dark:bg-gray-800 
                         text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
              />
              {errors.name && (
                <p className="text-red-600 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email (non modifiable)
                </label>
                <input
                  type="email"
                  value={currentUser?.email || ''}
                  disabled
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2
                           bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-400
                           cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Pour changer votre email, contactez un administrateur
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Téléphone
                </label>
                <input
                  type="tel"
                  value={profileForm.phone}
                  onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2
                           bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fonction
                </label>
                <input
                  type="text"
                  value={profileForm.fonction}
                  onChange={(e) => setProfileForm({ ...profileForm, fonction: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2
                           bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ville d'origine
                </label>
                <input
                  type="text"
                  value={profileForm.originCity}
                  onChange={(e) => setProfileForm({ ...profileForm, originCity: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2
                           bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                onClick={() => {
                  setIsEditingProfile(false);
                  setProfileForm({
                    name: currentUser?.name || '',
                    phone: currentUser?.phone || '',
                    fonction: currentUser?.fonction || '',
                    originCity: currentUser?.originCity || ''
                  });
                  setErrors({});
                }}
                variant="outline"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="primary"
                icon={Save}
              >
                Enregistrer
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            <InfoItem label="Email" value={currentUser?.email || 'Non renseigné'} />
            <InfoItem label="Téléphone" value={currentUser?.phone || 'Non renseigné'} />
            <InfoItem label="Fonction" value={currentUser?.fonction || 'Non renseignée'} />
            <InfoItem label="Ville d'origine" value={currentUser?.originCity || 'Non renseignée'} />
          </div>
        )}
      </div>

      {/* Changement de mot de passe */}
      <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center">
            <Lock size={20} className="mr-2" />
            Sécurité
          </h3>
          {!isEditingPassword && (
            <Button
              onClick={() => setIsEditingPassword(true)}
              variant="outline"
              size="sm"
            >
              Changer le mot de passe
            </Button>
          )}
        </div>

        {isEditingPassword ? (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mot de passe actuel *
              </label>
              <input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                className={`w-full border rounded-lg px-4 py-2 bg-white dark:bg-gray-800 
                         text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         ${errors.currentPassword ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
              />
              {errors.currentPassword && (
                <p className="text-red-600 text-sm mt-1">{errors.currentPassword}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nouveau mot de passe *
              </label>
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                className={`w-full border rounded-lg px-4 py-2 bg-white dark:bg-gray-800 
                         text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         ${errors.newPassword ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
              />
              {errors.newPassword && (
                <p className="text-red-600 text-sm mt-1">{errors.newPassword}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Confirmer le nouveau mot de passe *
              </label>
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                className={`w-full border rounded-lg px-4 py-2 bg-white dark:bg-gray-800 
                         text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
              />
              {errors.confirmPassword && (
                <p className="text-red-600 text-sm mt-1">{errors.confirmPassword}</p>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                onClick={() => {
                  setIsEditingPassword(false);
                  setPasswordForm({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                  setErrors({});
                }}
                variant="outline"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="primary"
                icon={Save}
              >
                Changer le mot de passe
              </Button>
            </div>
          </form>
        ) : (
          <p className="text-gray-600 dark:text-gray-400">
            Mot de passe : 
          </p>
        )}
      </div>
    </div>
  );
};

const InfoItem = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-600">
    <span className="text-gray-600 dark:text-gray-400">{label}</span>
    <span className="font-medium text-gray-800 dark:text-white">{value}</span>
  </div>
);

export default ProfileTab;